import { ChangeDetectionStrategy, Component, computed, DOCUMENT, inject } from '@angular/core';
import { StatusDotComponent } from '@commons/status-dot/status-dot.component';
import { environment } from '@env/environment';

const STAGING_ENV = 'staging';
const DEV_ENV = 'dev';
const STAGING_DOMAIN = 'staging.k8s.wizbii.us';
const DEV_DOMAIN = 'dev.k8s.wizbii.us';

type Env = typeof STAGING_ENV | typeof DEV_ENV | undefined;
type Domain = typeof STAGING_DOMAIN | typeof DEV_DOMAIN;

const ENV_MAP: Record<Domain, Env> = {
  [STAGING_DOMAIN]: STAGING_ENV,
  [DEV_DOMAIN]: DEV_ENV,
} as const;

@Component({
  selector: 'app-version',
  imports: [StatusDotComponent],
  template: `
    <p class="paragraph">
      <mark>
        <span class="env-dot-slot"><app-status-dot [pulse]="true" /></span>v {{ version }}
      </mark>
      @if (apiEnv(); as apiEnv) {
        <mark [class.orange]="!apiEnv.sameEnv">API: {{ apiEnv.env }}</mark>
      }
    </p>
  `,
  styles: [
    `
      :host {
        background-color: var(--wzb-bo-env-chip-bg);
        color: var(--wzb-bo-env-chip-fg);
        padding-inline: 1rem;
        border-top-left-radius: 10px;

        .paragraph {
          border: 1px solid;
          border-radius: 10px;
          margin: 3px;
          padding: 4px 0;
        }

        mark {
          display: block;
          font-size: small;
          color: var(--wzb-bo-env-chip-fg);
          background-color: transparent;
          font-weight: 600;

          &.orange {
            color: var(--wzb-bo-env-chip-warn-fg);
          }
        }

        // Non-prod "heartbeat": the status dot renders only off-prod —
        // --wzb-bo-env-dot is 'none' in prod (set in _colors.scss). The dot borrows
        // the chip's contrast color so it stays cohesive per env (white on staging,
        // dark on dev). The pulse/shape itself lives in StatusDotComponent.
        .env-dot-slot {
          display: var(--wzb-bo-env-dot, none);
          margin-inline-end: 6px;
          vertical-align: middle;

          --status-dot-color: var(--wzb-bo-env-accent-fg);
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VersionComponent {
  readonly version = environment.version;

  // SSR-safe window access: defaultView is null on the server (NestJS SSR), so a bare
  // `window` would crash server-side rendering. See _colors.scss / main.ts for the env theming.
  readonly #window = inject(DOCUMENT).defaultView;

  readonly apiEnv = computed(() => {
    let apiEnv: Env = undefined;

    if (environment.api.domain.includes(STAGING_DOMAIN)) {
      apiEnv = ENV_MAP[STAGING_DOMAIN];
    }
    if (environment.api.domain.includes(DEV_DOMAIN)) {
      apiEnv = ENV_MAP[DEV_DOMAIN];
    }

    // null on the server → empty origin → no env match (component renders prod-neutral).
    const origin = this.#window?.location.origin ?? '';

    if (origin.includes('localhost')) {
      return { env: apiEnv, sameEnv: false };
    }

    let env: Env = undefined;

    if (origin.includes(STAGING_DOMAIN)) {
      env = ENV_MAP[STAGING_DOMAIN];
    }

    if (origin.includes(DEV_DOMAIN)) {
      env = ENV_MAP[DEV_DOMAIN];
    }

    const sameEnv = apiEnv === env;

    if (env === undefined || apiEnv === undefined) {
      return;
    }

    return { env, sameEnv };
  });
}
